import React, { useState, useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import { styled } from '@mui/material/styles';

import Card from '@mui/material/Card';
import Footer from '../../pages/footer';
import Header from './header';
import Nav from './nav';
import AutoLogout from './AutoLogout';
import { getAcl, getMenuAccess, getFirstAccessibleMenuPath, getToken } from '../../utils/common';
import logger from '../../utils/logger';

const APP_BAR_MOBILE = 64;
const APP_BAR_DESKTOP = 69;

const StyledRoot = styled('div')({
  display: 'flex',
  minHeight: '100%',
  overflow: 'hidden',
  backgroundColor: '#f1fafe',
});

const Main = styled('main')(({ theme }) => ({
  flexGrow: 1,
  overflow: 'auto',
  minHeight: '100%',
  paddingTop: APP_BAR_MOBILE + 24,
  paddingBottom: theme.spacing(5),
  [theme.breakpoints.up('lg')]: {
    paddingTop: APP_BAR_DESKTOP + 24,
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
}));

export default function DashboardLayout() {
  const [open, setOpen] = useState(false);
  const [totaldata] = React.useState(0);
  const location = useLocation();
  const navigate = useNavigate();

  // Redirect guard: If user doesn't have access to current route, redirect to first accessible menu
  useEffect(() => {
    const checkAccessAndRedirect = async () => {
      // Skip if already on the root dashboard path without a child
      if (location.pathname === '/dashboard') {
        // Get ACL
        let acl = getAcl();
        const isValidAcl = (aclValue) => {
          if (!aclValue) return false;
          if (aclValue === '[]' || aclValue === 'null' || aclValue === 'undefined' || aclValue === '') return false;
          try {
            const parsed = JSON.parse(aclValue);
            return Array.isArray(parsed) && parsed.length > 0;
          } catch {
            return false;
          }
        };

        // Wait for ACL if needed
        if (!isValidAcl(acl)) {
          try {
            await getMenuAccess(true);
            await new Promise((resolve) => setTimeout(resolve, 500));
            acl = getAcl();
          } catch (error) {
            logger.error('DashboardLayout: Failed to fetch permissions:', error);
            return;
          }
        }

        if (isValidAcl(acl)) {
          const firstMenuPath = getFirstAccessibleMenuPath(acl);
          navigate(firstMenuPath, { replace: true });
        }
        return;
      }

      // Check if user has access to current route
      const currentPath = location.pathname;

      // List of sub-routes that inherit permissions from parent routes
      // These should NOT trigger redirects - they inherit permissions from their parent
      const subRoutes = ['detail-alert', 'add', 'edit', 'adduser'];

      // Map route name to menu route name
      const routeToMenuMap = {
        app: 'dashboard',
        dataset: 'data_set',
        user: 'user',
        action: 'action',
        alert: 'alert',
        report: 'report',
        information: 'information',
        activitylogs: 'activity',
        about: 'about',
        calendar: 'calendar',
      };

      // Check if this is a sub-route by checking if the last path segment is in subRoutes
      // or if it's a nested route like 'user/edit' or 'action/add' or 'action/edit'
      const pathParts = currentPath.split('/').filter((p) => p); // Remove empty strings
      const lastSegment = pathParts[pathParts.length - 1];
      const isSubRoute =
        subRoutes.includes(lastSegment) || currentPath.includes('/edit') || currentPath.includes('/add');

      let menuRouteName;
      let routeName;

      if (isSubRoute) {
        // For sub-routes, extract the parent route instead
        // e.g., /dashboard/alert/detail-alert -> check 'alert'
        // e.g., /dashboard/action/add -> check 'action'
        // e.g., /dashboard/user/edit -> check 'user'
        const parentRouteIndex = pathParts.length >= 2 ? pathParts.length - 2 : pathParts.length - 1;
        const parentRoute = pathParts[parentRouteIndex] || pathParts[pathParts.length - 1];
        routeName = parentRoute;
        menuRouteName = routeToMenuMap[parentRoute] || parentRoute;
      } else {
        // For top-level routes, use the last segment
        routeName = lastSegment;
        menuRouteName = routeToMenuMap[routeName] || routeName;
      }

      // Get ACL
      let acl = getAcl();
      const isValidAcl = (aclValue) => {
        if (!aclValue) return false;
        if (aclValue === '[]' || aclValue === 'null' || aclValue === 'undefined' || aclValue === '') return false;
        try {
          const parsed = JSON.parse(aclValue);
          return Array.isArray(parsed) && parsed.length > 0;
        } catch {
          return false;
        }
      };

      // Wait for ACL if needed
      if (!isValidAcl(acl)) {
        try {
          await getMenuAccess(true);
          await new Promise((resolve) => setTimeout(resolve, 500));
          acl = getAcl();
        } catch (error) {
          logger.error('DashboardLayout: Failed to fetch permissions:', error);
          return;
        }
      }

      if (!isValidAcl(acl)) {
        return;
      }

      // Check if user has access to current route
      try {
        const aclJson = JSON.parse(acl);
        const hasAccess = aclJson.some((item) => item.nama_menu === menuRouteName && item.read === true);

        // If user doesn't have access to current route, redirect to first accessible menu
        if (!hasAccess) {
          try {
            const firstMenuPath = getFirstAccessibleMenuPath(acl);
            // Use setTimeout to ensure navigate happens after current render cycle
            setTimeout(() => {
              navigate(firstMenuPath, { replace: true });
            }, 100);
          } catch (error) {
            logger.error('DashboardLayout: Error during redirect:', error);
          }
        }
      } catch (error) {
        logger.error('DashboardLayout: Error checking access:', error);
      }
    };

    // Only check if we have a token (user is authenticated)
    if (getToken() && getToken() !== 'undefined' && getToken() !== 'gada') {
      checkAccessAndRedirect();
    }
  }, [location.pathname, navigate]);

  return (
    <StyledRoot>
      <AutoLogout />
      <Header onOpenNav={() => setOpen(true)} totaldata={totaldata} />
      <Nav openNav={open} onCloseNav={() => setOpen(false)} />
      <Main>
        <Card
          style={{
            margin:
              location.pathname === '/dashboard/alert/detail-alert' || location.pathname === '/dashboard/dataset'
                ? 0
                : 20,
            paddingTop:
              location.pathname === '/dashboard/alert/detail-alert' || location.pathname === '/dashboard/dataset'
                ? 20
                : 0,
            padding:
              location.pathname === '/dashboard/alert/detail-alert' || location.pathname === '/dashboard/dataset'
                ? 0
                : 50,
            backgroundColor:
              location.pathname === '/dashboard/alert/detail-alert' || location.pathname === '/dashboard/dataset'
                ? 'transparent'
                : '',
            boxShadow:
              location.pathname === '/dashboard/alert/detail-alert' || location.pathname === '/dashboard/dataset'
                ? 'none'
                : '',
          }}
        >
          <Outlet />
        </Card>
      </Main>
      <Footer />
    </StyledRoot>
  );
}
